import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { auth, db } from "../FirebaseConfig";
import BankSelector from "../component/BankSelector";
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { FaUpload, FaImage, FaSpinner, FaCheck } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { Gift, AlertCircle, CheckCircle } from "lucide-react";

const Profile = (props) => {
  const user = auth.currentUser;
  const navigate = useNavigate();
  const location = useLocation();

  // Pre-fill from Landing page signup
  const pendingSeller = (() => {
    try {
      const raw = localStorage.getItem("ucart_pending_seller");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();

  // Get referral code from URL
  const getReferralCodeFromURL = () => {
    const params = new URLSearchParams(location.search);
    return params.get("ref") || "";
  };

  const [formData, setFormData] = useState({
    name: pendingSeller?.storeName || "",
    email: "",
    phoneNumber: pendingSeller?.whatsapp || "",
    bankName: pendingSeller?.bankName || "",
    bankCode: pendingSeller?.bankCode || "",
    accountNumber: pendingSeller?.accountNumber || "",
    accountName: pendingSeller?.accountName || "",
    profilePic: "",
    gender: "",
    role: pendingSeller?.role || "",
    // Checks Browser Memory FIRST, then URL, then stays blank
    referralCode: localStorage.getItem("ucart_referral_code") || getReferralCodeFromURL() || "",
  });

  const [loading, setLoading] = useState(true);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageUploaded, setImageUploaded] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showBankFields, setShowBankFields] = useState(pendingSeller?.role === "seller");
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupColor, setPopupColor] = useState("bg-red-600");

  // Referral validation states
  const [referralValidation, setReferralValidation] = useState({
    isValidating: false,
    isValid: null,
    referrerName: null,
    errorMessage: null,
  });

  useEffect(() => {
    if (!user) return;

    const fetchProfile = async () => {
      try {
        const docRef = doc(db, "profiles", user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const userData = docSnap.data();
          const role = userData.role;

          if (role === "seller") {
            navigate("/seller");
          } else if (role === "buyer") {
            navigate("/buyer");
          } else {
            navigate("/");
          }
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching profile:", error.message);
        const offlineMessage = !navigator.onLine
          ? "You're currently offline. Please check your connection."
          : "There was an error loading your profile.";
        setPopupMessage(offlineMessage);
        setPopupColor("bg-red-600");
        setShowPopup(true);
        setLoading(false);
      }
    };

    fetchProfile();
  }, [user, navigate]);

  // Validate referral code
  const validateReferralCode = async (code) => {
    if (!code || code.trim() === "") {
      setReferralValidation({
        isValidating: false,
        isValid: null,
        referrerName: null,
        errorMessage: null,
      });
      return;
    }

    setReferralValidation((prev) => ({
      ...prev,
      isValidating: true,
    }));

    try {
      const profilesRef = collection(db, "profiles");
      const q = query(
        profilesRef,
        where("referralCode", "==", code.trim().toUpperCase())
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setReferralValidation({
          isValidating: false,
          isValid: false,
          referrerName: null,
          errorMessage: "This referral code doesn't exist. Check and try again.",
        });
        return;
      }

      const referrerDoc = querySnapshot.docs[0];
      const referrerData = referrerDoc.data();

      if (referrerDoc.id === user.uid) {
        setReferralValidation({
          isValidating: false,
          isValid: false,
          referrerName: null,
          errorMessage: "You can't use your own referral code.",
        });
        return;
      }

      setReferralValidation({
        isValidating: false,
        isValid: true,
        referrerName: referrerData.name || referrerData.storeName || "A uCart User",
        errorMessage: null,
      });
    } catch (error) {
      console.error("Error validating referral code:", error);
      setReferralValidation({
        isValidating: false,
        isValid: false,
        referrerName: null,
        errorMessage: "Error validating code. Please try again.",
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "bankName") {
      const [bankName, bankCode] = value.split("|");
      setFormData((prev) => ({ ...prev, bankName, bankCode }));
    } else if (name === "referralCode") {
      const cleanCode = value.trim().toUpperCase();
      setFormData((prev) => ({ ...prev, referralCode: cleanCode }));
      // Validate in real-time with debouncing
      setTimeout(() => {
        if (cleanCode) {
          validateReferralCode(cleanCode);
        }
      }, 500);
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (name === "role") {
      setShowBankFields(value === "seller");
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const data = new FormData();
    data.append("file", file);
    data.append("upload_preset", "unsigned_ibuyunn");

    setImageUploading(true);
    setImageUploaded(false);

    try {
      const res = await fetch("https://api.cloudinary.com/v1_1/dm8uirujp/upload", {
        method: "POST",
        body: data,
      });
      if (!res.ok) throw new Error("Upload failed");
      const result = await res.json();
      setFormData({ ...formData, profilePic: result.secure_url });
      setImageUploaded(true);
    } catch (error) {
      console.error("Image upload failed:", error.message);
      const offlineMessage = !navigator.onLine
        ? "No internet connection. Please check your connection and try again."
        : "Image upload failed. Please try again.";
      setPopupMessage(offlineMessage);
      setPopupColor("bg-red-600");
      setShowPopup(true);
    } finally {
      setImageUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!user) return;

    const baseFields = ["name", "phoneNumber", "gender", "role", "profilePic"];
    const sellerFields = ["bankName", "bankCode", "accountNumber", "accountName"];

    const allFields =
      formData.role === "seller" ? [...baseFields, ...sellerFields] : baseFields;

    for (const field of allFields) {
      if (!formData[field] || formData[field].toString().trim() === "") {
        setPopupMessage(
          `Please fill the "${field.replace(/([A-Z])/g, " $1").toLowerCase()}" field.`
        );
        setPopupColor("bg-red-600");
        setShowPopup(true);
        return;
      }
    }

    // Validate referral code if provided
    if (formData.referralCode && referralValidation.isValid === false) {
      setPopupMessage("Please use a valid referral code or leave it empty.");
      setPopupColor("bg-red-600");
      setShowPopup(true);
      return;
    }

    setSubmitLoading(true);

    try {
      let referredBy = null;
      if (formData.referralCode && referralValidation.isValid) {
        const refCode = formData.referralCode.trim();
        const profilesRef = collection(db, "profiles");
        const q = query(profilesRef, where("referralCode", "==", refCode));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const referrerDoc = querySnapshot.docs[0];
          if (referrerDoc.id !== user.uid) {
            referredBy = referrerDoc.id;
          }
        }
      }
      let bankName = "";
      let bankCode = "";
      if (formData.bankName?.includes("|")) {
        [bankName, bankCode] = formData.bankName.split("|");
      } else {
        bankName = formData.bankName || "";
        bankCode = formData.bankCode || "";
      }

      const docRef = doc(db, "profiles", user.uid);
      await setDoc(docRef, {
        name: formData.name,
        phoneNumber: formData.phoneNumber,
        gender: formData.gender,
        role: formData.role,
        profilePic: formData.profilePic || "",
        bankName: bankName,
        bankCode: bankCode,
        accountNumber: formData.accountNumber || "",
        accountName: formData.accountName || "",
        userId: user.uid,
        userEmail: user.email,
        createdAt: serverTimestamp(),
        referredBy: referredBy,
        hasListed: false,           
        hasPurchased: false,        
        referralCode: null,
        totalEarnings: 0,
        sellerReferrals: 0,
        buyerReferrals: 0,
        verified: false,
        verification_status: null,
        blocked: false,
      });

      // Clear the pending seller data from localStorage
      localStorage.removeItem("ucart_pending_seller");
      
      // Deletes the referral code from memory after saving to database
      localStorage.removeItem("ucart_referral_code");

      setPopupMessage(
        referredBy
          ? `Profile saved! You were referred by someone. They will earn when you take action.`
          : "Profile saved successfully!"
      );
      setPopupColor("bg-green-600");
      setShowPopup(true);
      props.setSaveProfile(true);

      setTimeout(() => {
        setShowPopup(false);
        if (formData.role === "seller") {
          navigate("/seller");
        } else {
          navigate("/buyer");
        }
      }, 2000);
    } catch (error) {
      console.error("Error saving profile:", error.message);
      const offlineMessage = !navigator.onLine
        ? "No internet connection. Profile could not be saved."
        : "Error saving profile. Please try again.";
      setPopupMessage(offlineMessage);
      setPopupColor("bg-red-600");
      setShowPopup(true);
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading)
    return (
      <div className="text-white text-center mt-10">
        <div className="flex items-center justify-center h-screen bg-white">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-green-600 text-lg font-semibold animate-pulse">
              Loading, please wait...
            </p>
          </div>
        </div>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-green-600 rounded hover:bg-green-700"
        >
          Retry
        </button>
      </div>
    );

  return !props.saveProfile ? (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-cover bg-center py-10 px-4 text-white"
    >
      <div className="max-w-lg mx-auto bg-white/10 backdrop-blur p-6 rounded-xl">
        <h1 className="text-3xl font-bold mb-2 text-center">Complete Your Profile</h1>

        {/* Notice banner if pre-filled from Landing */}
        {pendingSeller && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-start gap-2.5 bg-green-500/10 border border-green-500/25 rounded-lg px-4 py-3 mb-5"
          >
            <span className="text-green-400 text-[18px] mt-0.5 flex-shrink-0">✓</span>
            <p className="text-[13px] text-green-300/80 leading-relaxed">
              Your store details have been pre-filled from your signup. Just add a profile photo
              and confirm your gender to get started.
            </p>
          </motion.div>
        )}

        <input
          type="text"
          name="name"
          placeholder="Full Name / Store Name"
          value={formData.name}
          onChange={handleChange}
          className="w-full mb-3 px-4 py-2 rounded bg-transparent border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-green-400"
        />
        <input
          type="number"
          name="phoneNumber"
          placeholder="WhatsApp / Phone Number"
          value={formData.phoneNumber}
          onChange={handleChange}
          className="w-full mb-3 px-4 py-2 rounded bg-transparent border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-green-400"
        />

        <select
          name="role"
          value={formData.role}
          onChange={handleChange}
          className="w-full mb-3 px-4 py-2 rounded bg-transparent border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-green-400"
          disabled={!!pendingSeller?.role}
        >
          <option value="" className="text-black">
            Select Role
          </option>
          <option value="buyer" className="text-black">
            Buyer
          </option>
          <option value="seller" className="text-black">
            Seller
          </option>
        </select>

        {showBankFields && (
          <>
            <BankSelector
              value={{ bankName: formData.bankName, bankCode: formData.bankCode }}
              onChange={(val) => setFormData((prev) => ({ ...prev, ...val }))}
            />
            <input
              type="text"
              name="accountNumber"
              placeholder="Account Number"
              value={formData.accountNumber}
              onChange={handleChange}
              className="w-full mb-3 px-4 py-2 rounded bg-transparent border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-green-400"
            />
            <input
              type="text"
              name="accountName"
              placeholder="Account Name"
              value={formData.accountName}
              onChange={handleChange}
              className="w-full mb-3 px-4 py-2 rounded bg-transparent border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-green-400"
            />

            {pendingSeller?.accountName && formData.accountName === pendingSeller.accountName && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center gap-2 bg-green-500/10 border border-green-500/20 rounded-lg px-3 py-2 mb-3"
              >
                <FaCheck className="w-4 h-4 text-green-400 flex-shrink-0" />
                <span className="text-[12px] text-green-300/80">
                  Account verified: <strong className="text-green-300">{formData.accountName}</strong>
                </span>
              </motion.div>
            )}
          </>
        )}

        <select
          name="gender"
          value={formData.gender}
          onChange={handleChange}
          className="w-full mb-3 px-4 py-2 rounded bg-transparent border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-green-400"
        >
          <option value="" className="text-black">
            Select Gender
          </option>
          <option value="male" className="text-black">
            Male
          </option>
          <option value="female" className="text-black">
            Female
          </option>
        </select>

        {/* Referral Code Input Section */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4 space-y-3"
        >
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Gift className="w-4 h-4 text-emerald-400" />
              <label className="text-sm font-semibold text-emerald-400 uppercase tracking-wider">
                Referral Code (Optional)
              </label>
            </div>
            <input
              type="text"
              name="referralCode"
              placeholder="Enter referral code (e.g., JOHN1234)"
              value={formData.referralCode}
              onChange={handleChange}
              className="w-full px-4 py-2 rounded bg-transparent border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-emerald-400 uppercase"
            />
            <p className="text-xs text-slate-400 mt-1">
              Know someone on uCart? Enter their referral code to support them!
            </p>
          </div>

          {/* Referral Validation Feedback */}
          <AnimatePresence>
            {referralValidation.isValidating && (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -5 }}
                className="flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 rounded-lg px-3 py-2"
              >
                <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-xs text-blue-300">Validating code...</span>
              </motion.div>
            )}

            {referralValidation.isValid === true && (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -5 }}
                className="flex items-start gap-2 bg-green-500/10 border border-green-500/30 rounded-lg px-3 py-2"
              >
                <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-green-300">
                  <p className="font-semibold">✓ Valid Code!</p>
                  <p>
                    Referred by: <strong>{referralValidation.referrerName}</strong>
                  </p>
                  <p className="text-green-400/80 mt-1">
                    They will earn when you take your first action!
                  </p>
                </div>
              </motion.div>
            )}

            {referralValidation.isValid === false && (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -5 }}
                className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2"
              >
                <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-red-300">
                  <p className="font-semibold">✗ Invalid Code</p>
                  <p>{referralValidation.errorMessage}</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Profile picture upload */}
        <div className="space-y-3 mb-4">
          <label className="block text-sm font-medium text-white">Profile Picture</label>
          <div className="relative border-2 border-dashed border-white/30 rounded-lg p-6 bg-white/5 cursor-pointer hover:border-white/50 transition-colors text-center">
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
            {formData.profilePic ? (
              <div className="relative">
                <img
                  src={formData.profilePic}
                  alt="Profile Preview"
                  className="w-32 h-32 object-cover rounded-full mx-auto border-4 border-white/20"
                />
                {imageUploaded && (
                  <div className="absolute -top-2 -right-2 bg-green-500 text-white p-1 rounded-full">
                    <FaCheck size={16} />
                  </div>
                )}
              </div>
            ) : imageUploading ? (
              <div className="flex flex-col items-center">
                <FaSpinner className="animate-spin text-white mb-2" size={32} />
                <p className="text-sm text-gray-300">Uploading...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center text-gray-300">
                <FaImage size={32} className="mb-2" />
                <p className="text-sm">Click to upload or drag and drop</p>
                <p className="text-xs mt-1">Recommended: 200x200 px</p>
              </div>
            )}
          </div>
        </div>

        <motion.button
          onClick={handleSubmit}
          disabled={submitLoading || imageUploading}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="w-full bg-green-600 hover:bg-green-700 px-4 py-2 rounded disabled:opacity-50 flex items-center justify-center space-x-2 font-semibold transition-all"
        >
          {submitLoading ? (
            <>
              <FaSpinner className="animate-spin" />
              <span>Saving...</span>
            </>
          ) : (
            <span>Save & Continue</span>
          )}
        </motion.button>

        {/* Terms & Conditions link */}
        <p className="text-center text-[12px] text-white/30 mt-5 leading-relaxed">
          By saving your profile you confirm you have read and agree to uCart's{" "}
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-green-400 hover:text-green-300 hover:underline transition-colors font-medium"
          >
            Terms & Conditions
          </a>{" "}
          and{" "}
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-green-400 hover:text-green-300 hover:underline transition-colors font-medium"
          >
            Privacy Policy
          </a>
          .
        </p>
      </div>

      {showPopup && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`fixed bottom-6 left-1/2 transform -translate-x-1/2 ${popupColor} text-white px-6 py-3 rounded-xl shadow-lg z-50`}
        >
          {popupMessage}
        </motion.div>
      )}
    </motion.div>
  ) : null;
};

export default Profile;